import { defineConfig, defineRecipe, defineSlotRecipe } from '@chakra-ui/react';

/**
 * Coach Hub button recipe — mirrors the vendored DS reference component
 * (design-system/components/core/Button.jsx). `base` gives ALL buttons the DS
 * label treatment: body font (Roboto), 600 weight, mixed-case (NOT uppercase),
 * DS radius, and a 1px transparent border so every variant shares one box model
 * (secondary/danger only swap the border COLOR, not the width). The DS disabled
 * state (muted #A9C6BE fill, white text, uniform across variants) lives here too.
 *
 * DS variants (primary / dark / secondary / ghost / danger) are ADDED here.
 * Chakra's built-in variants (solid / outline / subtle / surface / plain)
 * survive because createSystem deep-merges recipe configs — we do NOT redefine
 * defaultVariants, so the default (solid, md) is unchanged.
 *
 * Primary fill uses the raw DS vars (--brand-primary / --brand-primary-hover)
 * rather than brand.solid/brand.hover: those semantic tokens resolve to
 * brand.600/700 (one stop DARK of the DS spec), which would render the rest
 * state at the DS hover color. The raw vars match Button.jsx exactly.
 *
 * Those vars are NOT frozen at the Athlete Era default — theme.ts rewrites the
 * themeable subset on the root element from workspace.primary_color, so this
 * variant (and the filter chips built on it) follows the workspace's color.
 */
const buttonRecipe = defineRecipe({
  base: {
    fontFamily: 'body',
    fontWeight: '600',
    lineHeight: '1',
    borderRadius: 'l2',
    borderWidth: '1px',
    borderColor: 'transparent',
    _disabled: {
      bg: '#A9C6BE',
      color: '#fff',
      borderColor: 'transparent',
      opacity: 0.7,
      cursor: 'not-allowed',
    },
  },
  variants: {
    variant: {
      primary: {
        bg: 'var(--brand-primary)',
        color: 'brand.contrast',
        _hover: { bg: 'var(--brand-primary-hover)' },
      },
      dark: {
        bg: 'fg.strong',
        color: 'white',
        _hover: { opacity: 0.9 },
      },
      secondary: {
        bg: 'bg.panel',
        color: 'fg.strong',
        borderColor: 'border.emphasized',
        _hover: { bg: 'bg.subtle' },
      },
      ghost: {
        bg: 'transparent',
        color: 'fg',
        _hover: { bg: 'bg.subtle' },
      },
      danger: {
        bg: 'bg.panel',
        color: 'danger',
        borderColor: 'border.emphasized',
        _hover: { bg: 'danger.subtle' },
      },
    },
    size: {
      md: { h: 'auto', minW: 'auto', px: '18px', py: '10px', fontSize: 'ui' },
      sm: { h: 'auto', minW: 'auto', px: '12px', py: '7px', fontSize: 'sm' },
    },
  },
});

// DS field (Input.jsx fieldBase): WHITE surface (--bg-surface) + a 1px
// --border-strong border at rest; on focus the border goes brand and a 3px
// brand ring appears. Color/border/focus live in the `outline` variant (the
// default), NOT in `base`: Chakra's stock `outline` sets `bg: transparent` +
// `borderColor: border`, which would override anything placed in `base`.
const inputRecipe = defineRecipe({
  base: {
    fontFamily: 'body',
    borderRadius: 'l2',
    _placeholder: { color: 'fg.subtle' },
  },
  variants: {
    variant: {
      outline: {
        bg: 'bg.panel',
        borderColor: 'border.emphasized',
        _focusVisible: {
          borderColor: 'brand.solid',
          boxShadow: '0 0 0 3px var(--ring)',
        },
      },
    },
  },
});

// Same DS field treatment as inputRecipe (white surface + strong border).
const textareaRecipe = defineRecipe({
  base: {
    fontFamily: 'body',
    borderRadius: 'l2',
    _placeholder: { color: 'fg.subtle' },
  },
  variants: {
    variant: {
      outline: {
        bg: 'bg.panel',
        borderColor: 'border.emphasized',
        _focusVisible: {
          borderColor: 'brand.solid',
          boxShadow: '0 0 0 3px var(--ring)',
        },
      },
    },
  },
});

const headingRecipe = defineRecipe({
  base: {
    fontFamily: 'heading',
    fontWeight: '800',
    color: 'fg.strong',
    letterSpacing: 'display',
    textTransform: 'uppercase',
  },
});

const badgeRecipe = defineRecipe({
  base: {
    fontFamily: 'heading',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 'label',
    borderRadius: 'l1',
    bg: 'var(--badge-gray-bg)',
    color: 'var(--badge-gray-fg)',
  },
  variants: {
    variant: {
      danger:  { bg: 'danger.subtle',  color: 'danger'  },
      success: { bg: 'success.subtle', color: 'success' },
      warning: { bg: 'warning.subtle', color: 'warning' },
      info:    { bg: 'brand.soft',     color: 'brand.solid' },
    },
  },
});

// table is a slot recipe in Chakra v3. Anatomy keys (verified from node_modules):
//   table: ['root', 'header', 'body', 'row', 'columnHeader', 'cell', 'footer', 'caption']
const tableRecipe = defineSlotRecipe({
  slots: ['root', 'header', 'body', 'row', 'columnHeader', 'cell', 'footer', 'caption'],
  base: {
    columnHeader: {
      fontFamily: 'heading',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 'label',
      color: 'fg.muted',
    },
  },
});

// Slot recipe overrides — slots declared must be a subset of the installed
// anatomy keys. Actual anatomy keys (verified from node_modules):
//   nativeSelect: ['root', 'field', 'indicator']
//   checkbox:     ['root', 'label', 'control', 'indicator', 'group']
//   switch:       ['root', 'label', 'control', 'thumb', 'indicator']
// As with textarea, the field color/border/focus must live in the `outline`
// variant — Chakra's default `outline` sets the field `bg: transparent` +
// `borderColor: border` and would override anything placed in `base.field`.
const nativeSelectRecipe = defineSlotRecipe({
  slots: ['root', 'field', 'indicator'],
  base: {
    field: {
      fontFamily: 'body',
      borderRadius: 'l2',
    },
    indicator: { color: 'fg.muted' },
  },
  variants: {
    variant: {
      outline: {
        field: {
          bg: 'bg.panel',
          borderColor: 'border.emphasized',
          _focusVisible: {
            borderColor: 'brand.solid',
            boxShadow: '0 0 0 3px var(--ring)',
          },
        },
      },
    },
  },
});

// Collection-based Select (src/components/ui/select.tsx) had NO DS recipe, so
// its trigger/menu rendered with stock Chakra styling. This brings it to the
// same white DS field + bordered surface as NativeSelect.
//
// Declare the FULL @zag-js/select anatomy in `slots` (we only style trigger/
// content/item/indicator). Chakra merges slot arrays index-by-index, so a short
// list would overwrite — and drop — the leading default slots (root/label/
// positioner), collapsing the Select's `root` width. Listing every slot keeps
// the merged set complete.
const selectRecipe = defineSlotRecipe({
  slots: [
    'root', 'label', 'positioner', 'trigger', 'indicator', 'clearTrigger',
    'item', 'itemText', 'itemIndicator', 'itemGroup', 'itemGroupLabel',
    'list', 'content', 'control', 'valueText',
  ],
  base: {
    trigger: { fontFamily: 'body', borderRadius: 'l2' },
    content: {
      borderWidth: '1px',
      borderColor: 'border',
      borderRadius: 'l2',
      boxShadow: 'md',
    },
    item: { _highlighted: { bg: 'bg.subtle' } },
    indicator: { color: 'fg.muted' },
  },
  variants: {
    variant: {
      outline: {
        trigger: {
          bg: 'bg.panel',
          borderColor: 'border.emphasized',
          _expanded: {
            borderColor: 'brand.solid',
            boxShadow: '0 0 0 3px var(--ring)',
          },
          _focusVisible: {
            borderColor: 'brand.solid',
            boxShadow: '0 0 0 3px var(--ring)',
          },
        },
      },
    },
  },
});

const checkboxRecipe = defineSlotRecipe({
  slots: ['root', 'control', 'label'],
  base: {
    control: {
      borderRadius: 'l1',
      borderColor: 'border.emphasized',
      _checked: { bg: 'brand.solid', borderColor: 'brand.solid', color: 'brand.contrast' },
    },
    label: { fontFamily: 'body' },
  },
});

// DS Switch: off-track is --border-strong, on-track is the brand, thumb is a
// white disc with shadow-sm. Chakra only gets the brand on-state for free, so
// pin the off-track and thumb shadow to match the DS spec.
// DS field Label: body font, 14px, 600 weight, near-black (--text-strong).
// Chakra's stock label is 13px / medium / fg, so override the label slot. The
// helperText slot already matches the DS Help spec (12px / fg.muted) out of the
// box, and the requiredIndicator keeps Chakra's danger color. This lifts every
// Field label app-wide — including the fillable form blocks — to the DS spec.
//
// IMPORTANT: declare the FULL field anatomy in `slots`, not just `label`. A
// slot recipe's `slots` list replaces the default's, and any omitted slot loses
// its built-in styling — dropping `root` here collapses Field.Root's
// `width: 100%` + column layout, shrink-wrapping every field. We only override
// `label`; the rest are listed so their defaults survive.
const fieldRecipe = defineSlotRecipe({
  slots: ['root', 'label', 'helperText', 'errorText', 'requiredIndicator', 'input', 'select', 'textarea'],
  base: {
    label: {
      fontFamily: 'body',
      fontSize: 'ui',
      fontWeight: 'semibold',
      color: 'fg.strong',
    },
  },
});

const switchRecipe = defineSlotRecipe({
  slots: ['root', 'control', 'thumb', 'label'],
  base: {
    control: {
      bg: 'border.emphasized',
      _checked: { bg: 'brand.solid' },
    },
    thumb: { boxShadow: 'sm' },
    label: { fontFamily: 'body' },
  },
});

// ── Navigation ───────────────────────────────────────────────────────────────
// DS Navigation card components. TopNav is composed in src/components/navbar/;
// Tabs + Breadcrumbs are bridged here so all call sites inherit the DS spec.

// tabs: ['root','list','trigger','content','contentGroup','indicator'] (verified)
// DS Tabs are the underline (`line`) style: a 1px bottom border on the list,
// 15px/600 triggers, inactive fg.muted, active = brand text + a 2px brand
// underline. Chakra's stock `line` already gives the border + fg.muted inactive,
// but its selected state uses `fg` (dark) text and a `colorPalette.solid`
// indicator — so we repoint selected text to brand and pin the underline color
// to brand regardless of the trigger's colorPalette (brand is the only themeable
// color). The 15px size lives in the default `size: md`, because that size group
// otherwise forces textStyle `sm` (13px) and would clobber a fontSize set lower
// in the cascade.
const tabsRecipe = defineSlotRecipe({
  slots: ['root', 'list', 'trigger', 'content', 'contentGroup', 'indicator'],
  variants: {
    variant: {
      line: {
        trigger: {
          fontFamily: 'body',
          fontWeight: '600',
          _selected: {
            color: 'brand.solid',
            _horizontal: { '--indicator-color': '{colors.brand.solid}' },
          },
        },
      },
    },
    size: {
      md: { trigger: { textStyle: 'md' } },
    },
  },
});

// breadcrumb: ['root','list','item','link','currentLink','ellipsis','separator']
// DS Breadcrumbs: a 14px (ui) row, muted links, a brand + 600 current crumb, and
// faint '/' separators. The wrapper (src/components/ui/breadcrumb.tsx) supplies
// the '/' glyph and the items API; this recipe owns the colors/sizing.
const breadcrumbRecipe = defineSlotRecipe({
  slots: ['root', 'list', 'item', 'link', 'currentLink', 'ellipsis', 'separator'],
  base: {
    list: { fontFamily: 'body', fontSize: 'ui', gap: '8px' },
    link: {
      color: 'fg.muted',
      textDecoration: 'none',
      _hover: { color: 'fg', textDecoration: 'none' },
    },
    currentLink: { color: 'brand.solid', fontWeight: '600' },
    separator: { color: 'fg.subtle' },
  },
});

// ── Feedback & Overlays ──────────────────────────────────────────────────────
// The DS feedback primitives (Toast, Tooltip, Menu, Modal/SlideOver) ship their
// own visual spec. Chakra's stock recipes diverge from it, so we override here
// rather than re-styling each call site. Anatomy keys verified from node_modules.

// toast: ['root','title','description','indicator','closeTrigger','actionTrigger']
// DS Toast: white surface, 1px border, a 4px LEFT accent + colored icon chip per
// variant (success/error/info), shadow-md. Chakra's default instead floods the
// whole toast with the solid status color — we neutralize that and apply the DS
// treatment. `--toast-accent` is set on root per data-type and inherits down to
// the indicator chip + left border.
const toastRecipe = defineSlotRecipe({
  slots: ['root', 'title', 'description', 'indicator', 'closeTrigger'],
  base: {
    root: {
      '--toast-accent': '{colors.fg.muted}',
      bg: 'bg.panel',
      color: 'fg',
      borderWidth: '1px',
      borderColor: 'border',
      borderLeftWidth: '4px',
      borderLeftColor: 'var(--toast-accent)',
      borderRadius: 'l2',
      boxShadow: 'md',
      // Neutralize Chakra's solid-fill variants; keep the surface, swap the accent.
      '&[data-type=success]': { bg: 'bg.panel', color: 'fg', '--toast-accent': 'var(--success)' },
      '&[data-type=error]': { bg: 'bg.panel', color: 'fg', '--toast-accent': 'var(--danger)' },
      '&[data-type=warning]': { bg: 'bg.panel', color: 'fg', '--toast-accent': 'var(--warning)' },
      '&[data-type=info]': { bg: 'bg.panel', color: 'fg', '--toast-accent': '{colors.brand.solid}' },
    },
    indicator: {
      flexShrink: '0',
      boxSize: '5',
      borderRadius: 'full',
      bg: 'var(--toast-accent)',
      color: 'white',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      _icon: { boxSize: '3' },
    },
    title: { fontFamily: 'body', fontWeight: '600', textStyle: 'ui', color: 'fg.strong' },
    description: { fontFamily: 'body', textStyle: 'sm', color: 'fg.muted', opacity: '1' },
    closeTrigger: { color: 'fg.subtle' },
  },
});

// tooltip: ['trigger','arrow','arrowTip','positioner','content']
// DS Tooltip: dark bubble (--text-strong), white text, sm radius, shadow-md.
const tooltipRecipe = defineSlotRecipe({
  slots: ['content', 'arrowTip'],
  base: {
    content: {
      '--tooltip-bg': '{colors.fg.strong}',
      bg: 'var(--tooltip-bg)',
      color: 'white',
      fontFamily: 'body',
      fontWeight: '500',
      borderRadius: 'l1',
      boxShadow: 'md',
    },
    arrowTip: { borderColor: 'var(--tooltip-bg)' },
  },
});

// menu: content + item + separator (full anatomy is larger; subset is fine)
// DS Menu: surface card, 1px border, shadow-md, sm-radius items, hover = bg-subtle,
// separator = --border.
const menuRecipe = defineSlotRecipe({
  slots: ['content', 'item', 'separator'],
  base: {
    content: {
      boxShadow: 'md',
      borderWidth: '1px',
      borderColor: 'border',
      borderRadius: 'l2',
    },
    item: { _highlighted: { bg: 'bg.subtle' } },
    separator: { bg: 'border' },
  },
});

// dialog/drawer: ['backdrop','content', …]. DS scrim is the navy --bg-overlay,
// not Chakra's blackAlpha.500. Centered Modal uses lg radius + shadow-lg; the
// SlideOver (drawer) is a flush full-height panel (no radius), shadow-lg.
const dialogRecipe = defineSlotRecipe({
  slots: ['backdrop', 'content'],
  base: {
    backdrop: { bg: 'bg.overlay' },
    content: { borderRadius: 'l3', boxShadow: 'lg' },
  },
});

const drawerRecipe = defineSlotRecipe({
  slots: ['backdrop', 'content'],
  base: {
    backdrop: { bg: 'bg.overlay' },
    content: { boxShadow: 'lg' },
  },
});

// emptyState: ['root','content','indicator','title','description']
// DS EmptyState: the icon sits in a 64px subtle disc (faint glyph), a display-tier
// title, and a muted message. Chakra's default renders a bare oversized icon and a
// body-font title — this brings the compound component up to the DS spec for all
// call sites at once.
const emptyStateRecipe = defineSlotRecipe({
  slots: ['indicator', 'title'],
  base: {
    indicator: {
      boxSize: '64px',
      borderRadius: 'full',
      bg: 'bg.subtle',
      color: 'fg.subtle',
    },
    title: { fontFamily: 'heading', fontWeight: '700', color: 'fg.strong' },
  },
});

export const dsRecipesConfig = defineConfig({
  theme: {
    recipes: {
      button: buttonRecipe,
      input: inputRecipe,
      textarea: textareaRecipe,
      heading: headingRecipe,
      badge: badgeRecipe,
    },
    slotRecipes: {
      tabs: tabsRecipe,
      breadcrumb: breadcrumbRecipe,
      nativeSelect: nativeSelectRecipe,
      select: selectRecipe,
      field: fieldRecipe,
      checkbox: checkboxRecipe,
      switch: switchRecipe,
      table: tableRecipe,
      toast: toastRecipe,
      tooltip: tooltipRecipe,
      menu: menuRecipe,
      dialog: dialogRecipe,
      drawer: drawerRecipe,
      emptyState: emptyStateRecipe,
    },
  },
});
